import Konva from 'konva';
import type { ComponentDef } from '../../core/library/component-def.js';
import { addUnifiedValueLabel } from './value-label.js';

/**
 * Renderer del cuerpo de una batería en formato PP3 (el clásico
 * "petaca" de 9 V). La FORMA es la de ese encapsulado, pero el
 * componente es genérico: la tensión se toma del valor de la instancia
 * y se edita en sus propiedades, así que este renderer sólo pinta el
 * `labelText` que recibe.
 *
 * Visión superior "tumbada" en el esquema: rectángulo con esquinas
 * suaves, dos terminales snap en la parte inferior (+ y −) alineados
 * con los pines (x = ±6.35 mm) y una etiqueta central con el valor.
 */
export function renderBattery9VBody(
  def: ComponentDef,
  labelText: string,
  rotationDeg: number = 0,
  flipped: boolean = false,
): Konva.Group {
  const g = new Konva.Group();

  // Escogemos length_mm × width_mm como el rectángulo visible en el
  // esquema (17.5 × 26.5 en la def).
  const w = def.body.length_mm ?? 17.5;
  const h = def.body.width_mm ?? 26.5;
  const cr = def.body.corner_radius_mm ?? 2;

  const bodyColor = def.appearance.body_color;
  // Rango del degradado más amplio y hacia el claro que hacia el
  // oscuro: la carcasa se lee como gris "medio" en vez de casi negro,
  // que era la queja del usuario. Sombra sólo un pelín más oscura para
  // dar volumen sin ennegrecer.
  const light = mix(bodyColor, '#ffffff', 0.45);
  const dark = mix(bodyColor, '#000000', 0.20);

  // Carcasa principal con degradado vertical.
  const shell = new Konva.Rect({
    x: -w / 2,
    y: -h / 2,
    width: w,
    height: h,
    cornerRadius: cr,
    fillLinearGradientStartPoint: { x: 0, y: -h / 2 },
    fillLinearGradientEndPoint: { x: 0, y: h / 2 },
    fillLinearGradientColorStops: [
      0, dark,
      0.15, bodyColor,
      0.55, light,
      1, dark,
    ],
    stroke: '#000',
    strokeWidth: 0.15,
    listening: true,
  });
  shell.setAttr('nodeKind', 'body');
  g.add(shell);

  // Base plateada donde emergen las patillas (parte inferior en local).
  const baseH = 2.4;
  const base = new Konva.Rect({
    x: -w / 2 + 0.4,
    y: -h / 2 + h - baseH - 0.3,
    width: w - 0.8,
    height: baseH,
    // Esquinas bien redondeadas: casi una cápsula.
    cornerRadius: baseH / 2,
    fill: def.appearance.metal_color ?? '#c8c8c8',
    stroke: '#00000060',
    strokeWidth: 0.06,
    listening: false,
  });
  g.add(base);

  // Símbolos + y − serigrafiados en la PARTE SUPERIOR de la carcasa,
  // alineados en X con sus pines respectivos (x = ±6.35).
  const symbolFont = 3.4;
  const symbolY = -h / 2 + symbolFont * 0.75;
  // Al invertir con flipped=true el grupo lleva scaleY(-1) y el texto
  // saldría del revés; lo contrarrestamos localmente. Konva escala
  // respecto al origen del nodo, así que la `y` también se compensa
  // para que el texto siga centrado en `symbolY`.
  const textScaleY = flipped ? -1 : 1;
  const textY = symbolY + (flipped ? symbolFont / 2 : -symbolFont / 2);
  const addSymbol = (cx: number, text: string, color: string) => {
    g.add(new Konva.Text({
      x: cx - symbolFont / 2,
      y: textY,
      width: symbolFont,
      height: symbolFont,
      text,
      fontSize: symbolFont,
      fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
      fontStyle: 'bold',
      fill: color,
      align: 'center',
      verticalAlign: 'middle',
      scaleY: textScaleY,
      listening: false,
    }));
  };
  addSymbol(-6.35, '+', '#ff4d4d');
  addSymbol(6.35, '−', '#4da3ff');

  // Etiqueta con el valor centrada, orientada al usuario incluso si el
  // grupo está rotado o invertido. El texto viene ya resuelto desde
  // `component-renderer` (valor de instancia → default de la def), así
  // que aquí no se fuerza ningún valor: la batería es genérica y su
  // tensión se edita en las propiedades de cada instancia.
  const labelGroup = new Konva.Group();
  const font = def.appearance.label?.font_size_mm ?? 4;
  addUnifiedValueLabel(labelGroup, labelText, font, w * 0.9);
  // Contra-rota / des-espeja para que el texto salga siempre legible.
  labelGroup.rotation(-rotationDeg);
  if (flipped) labelGroup.scaleY(-1);
  g.add(labelGroup);

  return g;
}

function mix(a: string, b: string, t: number): string {
  const pa = hexToRgb(a);
  const pb = hexToRgb(b);
  const r = Math.round(pa[0] + (pb[0] - pa[0]) * t);
  const g = Math.round(pa[1] + (pb[1] - pa[1]) * t);
  const bl = Math.round(pa[2] + (pb[2] - pa[2]) * t);
  return `#${((1 << 24) + (r << 16) + (g << 8) + bl).toString(16).slice(1)}`;
}
function hexToRgb(h: string): [number, number, number] {
  const s = h.replace('#', '');
  const v = parseInt(s.length === 3 ? s.split('').map((c) => c + c).join('') : s, 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
}
